import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  FileText,
  Building,
  Package,
  Settings,
  HelpCircle,
  Calendar,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUrlProject } from "@/hooks/useUrlProject";
import { useAppStore } from "@/lib/store";

interface SidebarProps {
  open: boolean;
  toggleSidebar: () => void;
}

export default function Sidebar({ open, toggleSidebar }: SidebarProps) {
  const [location] = useLocation();
  const isMobile = useIsMobile();
  const { urlProjectId } = useUrlProject();
  const { selectedProject } = useAppStore();

  // Handle sidebar visibility on mobile
  useEffect(() => {
    if (isMobile) {
      const sidebar = document.getElementById("sidebar");
      if (sidebar) {
        if (open) {
          sidebar.classList.remove("hidden");
          sidebar.classList.add("fixed", "left-0", "top-0", "z-50");
        } else {
          sidebar.classList.add("hidden");
        }
      }
    }
  }, [open, isMobile]);

  const menuItems = [
    {
      path: "dashboard",
      label: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5 mr-3" />,
    },
    {
      path: "documents",
      label: "Documents",
      icon: <FileText className="h-5 w-5 mr-3" />,
    },
    {
      path: "tender-packages",
      label: "Tender Packages",
      icon: <Package className="h-5 w-5 mr-3" />,
    },
    {
      path: "tender-returns",
      label: "Tender Returns",
      icon: <FileText className="h-5 w-5 mr-3" />,
    },
    {
      path: "tes",
      label: "TES",
      icon: <Calendar className="h-5 w-5 mr-3" />,
    },
    {
      path: "reports",
      label: "Reports",
      icon: <FileText className="h-5 w-5 mr-3" />,
    },
    {
      path: "companies",
      label: "Companies",
      icon: <Building className="h-5 w-5 mr-3" />,
    },
  ];

  const settingsItems = [
    {
      path: "/settings",
      label: "Settings",
      icon: <Settings className="h-5 w-5 mr-3" />,
    },
    {
      path: "/help",
      label: "Help",
      icon: <HelpCircle className="h-5 w-5 mr-3" />,
    },
  ];

  return (
    <div
      id="sidebar"
      className={cn(
        "w-full bg-gray-700 md:min-h-screen overflow-y-auto md:fixed md:left-0 md:top-0 md:z-50 transition-all duration-300 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent hover:scrollbar-thumb-gray-400",
        open ? "md:w-64" : "md:w-16"
      )}
    >
      <div className="p-4 flex items-center justify-between md:justify-start">
        <div className="flex items-center">
          <span className={cn(
            "text-white font-semibold text-lg transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0 md:hidden"
          )}>
            Tectonix Procurement
          </span>
        </div>
      </div>

      <nav className="mt-2">
        {menuItems.map((item) => {
          // Create project-aware URLs following ACC pattern
          const projectId = urlProjectId || selectedProject?.id;
          const href = projectId ? `/${item.path}/projects/${projectId}` : `/${item.path}`;
          
          // Check if current location matches this menu item
          const isActive = location === href || 
            location.startsWith(`/${item.path}/projects/`) ||
            (item.path === "dashboard" && (location === "/" || location === "/dashboard"));
          
          return (
            <Link key={item.path} href={href}>
              <div
                className={cn(
                  "flex items-center px-6 py-3 hover:bg-gray-700 hover:text-white cursor-pointer transition-all duration-300",
                  isActive ? "text-white bg-blue-600" : "text-gray-300",
                  !open && "md:justify-center md:px-3"
                )}
                title={!open ? item.label : undefined}
              >
                {item.icon}
                <span className={cn(
                  "transition-opacity duration-300",
                  open ? "opacity-100" : "opacity-0 md:hidden"
                )}>
                  {item.label}
                </span>
              </div>
            </Link>
          );
        })}

        <div className={cn(
          "px-4 py-2 mt-4 transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0 md:hidden"
        )}>
          <span className="text-xs uppercase font-semibold tracking-wider text-gray-400">
            Settings
          </span>
        </div>

        {settingsItems.map((item) => {
          // Settings items: some are project-aware, some are global
          const projectId = urlProjectId || selectedProject?.id;
          const isProjectAware = item.path === "/settings";
          const href = (isProjectAware && projectId) ? 
            `${item.path}/projects/${projectId}` : 
            item.path;
          
          const isActive = location === href || 
            (isProjectAware && location.startsWith(`${item.path}/projects/`));
          
          return (
            <Link key={item.path} href={href}>
              <div
                className={cn(
                  "flex items-center px-6 py-3 hover:bg-gray-700 hover:text-white cursor-pointer transition-all duration-300",
                  isActive ? "text-white bg-blue-600" : "text-gray-300",
                  !open && "md:justify-center md:px-3"
                )}
                title={!open ? item.label : undefined}
              >
                {item.icon}
                <span className={cn(
                  "transition-opacity duration-300",
                  open ? "opacity-100" : "opacity-0 md:hidden"
                )}>
                  {item.label}
                </span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Toggle Sidebar Button */}
      <div className="absolute bottom-4 left-0 right-0 px-2">
        <button
          onClick={toggleSidebar}
          className={cn(
            "flex items-center justify-center text-white rounded-md transition-all duration-200 shadow-lg",
            open 
              ? "w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 border-2 border-blue-500" 
              : "w-full md:w-10 md:h-10 md:rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-blue-500 md:shadow-xl"
          )}
          title={open ? "Collapse Sidebar" : "Expand Sidebar"}
        >
          {open ? (
            <>
              <PanelLeftClose className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-full h-full">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <span className="text-sm md:hidden font-medium">Expand</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
